import React, { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  useSearchParams,
  useNavigate,
  useHref as useRouterHref,
  NavigateOptions,
  To,
} from "react-router";

import { useTrackingCode } from "react-hubspot-tracking-code-hook";

import axios from "axios";
import "./App.css";
import "./styles/fonts.css";
import "./styles/colors.css";
import "./styles/highlight.css";
import Login from "./pages/Auth/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import { ConsentGate } from "@/Components/Consent/ConsentGate";
import { ConsentReconciler } from "@/Components/Consent/ConsentReconciler";
import { AdminFeedback } from "@/Components/AdminFeedback/AdminFeedback";
import { getInfoFromToken } from "./apiCalls";

import { StateContext } from "./context/StateContext";

import ResetPassword from "./pages/Auth/ResetPassword/ResetPassword";
import * as Sentry from "@sentry/react";
import useAccountInfo from "./hooks/useAccountInfo";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";
import { api, queryClient } from "./lib/api-client";
import useUserPermissionsStore from "./stores/useUserPermissionsStore";
import usePreferredLanguageStore from "./stores/usePreferredLanguageStore";
import { Register } from "./pages/Auth/Register/Register";

import "dayjs/locale/sl";
import "dayjs/locale/en";
import "dayjs/locale/de";
import "dayjs/locale/hr";
import "dayjs/locale/it";
import "dayjs/locale/es";
import "dayjs/locale/sr";
import "dayjs/locale/fr";
import "dayjs/locale/bs";
import "dayjs/locale/ru";
import "dayjs/locale/uk";

import { Helmet } from "react-helmet-async";
import { HeroUIProvider, Spinner, ToastProvider } from "@heroui/react";
import { I18nProvider } from "@heroui-v3/react";
import { AppVersionChecker } from "./AppVersionChecker";
import { useLingui } from "@lingui/react";
import ForgotPassword from "./pages/Auth/ForgotPassword/ForgotPassword";
import { GetRedirectLink } from "./server-types";
import { Maintenance } from "./pages/Maintenance/Maintenance";
import { SwReset } from "./pages/Dashboard/SwReset";
import { StartupRecovery } from "@/Components/StartupRecovery";
import { useStartupWatchdog } from "@/hooks/useStartupWatchdog";
import {
  clearAccessToken,
  forceRelogin,
  getCleanAccessToken,
  setAccessToken,
} from "@/utils/auth-token";
import { describeErrorForLog } from "./utils/sentry-error";
import { sentryLog } from "./utils/sentry-utils";
import { beforeSendFilter } from "@/utils/sentry-before-send";
import { redactLog } from "@/utils/sentry-log-redaction";
import { SENTRY_DSN } from "@/utils/sentry-dsn";
import { MfaReauthProvider } from "./context/MfaReauthContext";
import {
  getBrowserPreferredLocale,
  getClosestSupportedLocale,
  getLocaleSearchParam,
} from "@/utils/locale-utils";
import { ThemeProvider } from "next-themes";

declare module "@react-types/shared" {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

// Enable Sentry by default; set VITE_SENTRY to "false" to disable
const enableSentry = import.meta.env.VITE_SENTRY !== "false";

Sentry.init({
  enabled: enableSentry,
  dsn: SENTRY_DSN,
  integrations: [
    // https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v8/
    ...(enableSentry
      ? [
          Sentry.reactRouterBrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
        ]
      : []),
  ],

  enableLogs: true,

  // Replay is only ever added for Lime admin sessions (utils/sentry-replay).
  // These rates make it buffer instead of record, and keep one on an error.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Backstop: strip credentials from every log attribute tree, so one missed
  // call site cannot ship a session JWT. See ./utils/sentry-log-redaction.
  beforeSendLog: redactLog,

  // Filter and re-level expected client errors (printer/QZ failures,
  // cancellations, 4xx/5xx, stale chunks). Extracted and unit-tested in
  // ./utils/sentry-before-send.
  beforeSend: beforeSendFilter,

  ignoreErrors: [
    // extensions
    "TypeError: can't access dead object",
    "NS_ERROR_FAILURE",
    // Browser-extension content scripts call the WebExtensions messaging API
    // (`browser.runtime.sendMessage`), which rejects with "Invalid call to
    // runtime.sendMessage(). Tab not found." when the target tab is gone (e.g.
    // the form page navigating/closing on iOS Safari). It surfaces via
    // onunhandledrejection with no stacktrace; we never call that API, so there
    // is nothing on our page to fix (DEV-10233).
    /Invalid call to runtime\.sendMessage/,
    // Firefox throws NS_ERROR_UNEXPECTED from `window.location.href` when the
    // fire-and-forget form redirect navigation races page teardown — a benign
    // Gecko navigation quirk, not an app bug.
    "NS_ERROR_UNEXPECTED",
    // Password-manager / autofill extension content scripts (FormMetadata.js)
    // throwing inside the page, caught by our global onerror handler.
    /_logicalFormForControl/,
    // React & DOM manipulation noise
    "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
    "NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
    // Chrome Translate or third-party DOM modifications
    "Unexpected value in Node.removeChild call",
    // react-aria's FocusScope restore-focus runs in a requestAnimationFrame
    // after an overlay (HeroUI modal/popover/select) closes; if the scope's
    // elements are already unmounted it dereferences an empty `scope[0]`. A
    // harmless focus-management race in third-party code — our own code never
    // touches previousElementSibling.
    /previousElementSibling/,
    "Cannot read properties of null (reading '...')",
    // Third party ad/analytics/CDN script errors
    "ResizeObserver loop limit exceeded",
    "Script error",
    "JavaScript error: Script error",
    // In-app browser webviews inject their own bridge scripts that reference
    // private globals (e.g. Snapchat's `SCDynimacBridge`). When those run on our
    // page they throw a ReferenceError caught by the global onerror handler —
    // pure third-party noise, never defined in our code.
    /SCDynimacBridge/,
    // Instagram/Facebook in-app browsers inject a performance logger that flushes
    // telemetry over a JS↔Java bridge on `beforeunload`. When we navigate (form
    // redirect) the WebView tears down mid-flush and the bridge call throws — pure
    // third-party noise from their injected code, nothing on our page to fix. The
    // trailing text varies by WebView build ("Java bridge method invocation error",
    // "Java exception was raised during method invocation"), so match the stable
    // "Error invoking postMessage" prefix instead of one exact variant.
    /Error invoking postMessage/,
    // Instagram/Facebook in-app WebViews evaluate an injected/proxied script
    // whose source failed to load and returned a CDN error page ("Service
    // Unavailable"); parsing that text as JS trips on the bare word. The script
    // has no source URL, so the frame is our document (`<!doctype html>`) rather
    // than a chunk — pure third-party noise, nothing on our page to fix.
    /Unexpected identifier 'Unavailable'/,
    // Network line noise or unhelpful browser messages
    "NetworkError",
    "Network Error",
    "SecurityError",
    "Security Error",
    "The quota has been exceeded",
    /^DOMException/,

    // maximum call stack size exceeded
    "Maximum call stack size exceeded",
  ],

  // Replaces the deprecated `sendDefaultPii` (removed in v11). Every category
  // is listed because the SDK resolves an explicit `dataCollection` from its
  // permissive defaults, so an omitted key re-enables that category.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: false, response: false },
    httpBodies: [],
    urlQueryParams: false,
    stackFrameVariables: false,
  },

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.02,
  tracePropagationTargets: [
    /^https?:\/\/localhost(:\d+)?\//,
    /^https:\/\/api\.lime-booking\.com/,
  ],
});

// Module scope: wrapping inside App() makes a new Routes type every render and remounts the dashboard.
const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

/**
 * Custom useHref wrapper that handles external URLs properly.
 * React Router's useHref treats all URLs as relative paths, which breaks external links.
 * This wrapper detects external URLs and returns them unchanged.
 */
const isExternalUrl = (url: string): boolean => {
  return /^(https?:\/\/|\/\/|mailto:|tel:|sms:)/.test(url);
};

const useHref = (
  to: To,
  options?: Parameters<typeof useRouterHref>[1],
): string => {
  const routerHref = useRouterHref(to, options);

  // If `to` is a string and is an external URL, return it as-is
  if (typeof to === "string" && isExternalUrl(to)) {
    return to;
  }

  // Otherwise, use React Router's href resolution
  return routerHref;
};

function App() {
  const navigate = useNavigate();

  const { setPathPageView, setTrackPageView, setIdentity } = useTrackingCode();

  const { handleStateChange } = useContext(StateContext);

  const [clientState, setClientState] = useState({
    isAuth: false,
    isLoading: true,
    onboardingDone: false,
    token: "",
  });

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { i18n } = useLingui();
  const { locale } = i18n;

  // Toasts are fixed-positioned with JS-computed offsets, so the body's
  // safe-area padding doesn't reach them — offset them explicitly.
  const safeAreaInsets = useSafeAreaInsets();

  // Tracked as state (not read at render time) so toast placement/offset
  // follow rotation and window resizes across the 768px breakpoint.
  const [isWideViewport, setIsWideViewport] = useState(
    () => window.matchMedia("(min-width: 769px)").matches,
  );
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 769px)");
    const onChange = (event: MediaQueryListEvent) =>
      setIsWideViewport(event.matches);
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  const { mutateAsync: verifyToken } = api.auth.useVerifyTempToken();

  const { setAccountInfo, accountInfo } = useAccountInfo((state) => state);

  const initLoadPermissions = useUserPermissionsStore(
    (state) => state.initLoadPermissions,
  );

  const { loadPreferredLanguage, setPreferredLanguage, preferredLanguage } =
    usePreferredLanguageStore((state) => state);

  const { isFetching: isFeaturesLoading, refetch: refetchFeatures } =
    api.client.useGetClientFeatures(clientState.isAuth);

  const { isStalled: isStartupStalled, markStep } = useStartupWatchdog(
    clientState.isLoading || isFeaturesLoading,
  );

  const checkAuth = async () => {
    setClientState({ ...clientState, isLoading: true });

    const token = getCleanAccessToken();

    if (token !== "") {
      try {
        markStep("verify-token");
        const verify = await getInfoFromToken(token);
        if (!verify) {
          throw new Error("Invalid token");
        }

        markStep("load-permissions");
        await initLoadPermissions();

        // set global account info state
        setAccountInfo(verify.data[0]);
        handleStateChange(verify.data);

        markStep("load-preferred-language");
        await loadPreferredLanguage();

        markStep("client-features");
        refetchFeatures();

        Sentry.setContext("user", {
          clientId: verify.data[0].clientId,
          userId: verify.data[0].userId,
          pricingTierId: verify.data[0].FK_pricingTierId,
          onboardingDone: verify.data[0].onboardingDone,
        });

        Sentry.setTag("clientId", verify.data[0].clientId);

        setIdentity(verify.data[0].userEmail, {
          clientId: verify.data[0].clientId,
          userId: verify.data[0].userId,
          pricingTierId: verify.data[0].FK_pricingTierId,
          email: verify.data[0].userEmail,
          name: verify.data[0].userName,
          lastName: verify.data[0].userLastName,
          gsm: verify.data[0].userPhone,
        });

        setClientState({
          ...clientState,
          isAuth: true,
          onboardingDone: verify.data[0].onboardingDone,
          isLoading: false,
          token: token,
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 503) {
          setClientState((prevState) => ({
            ...prevState,
            isLoading: false,
          }));
          return;
        }

        setClientState((prevState) => ({
          ...prevState,
          isAuth: false,
          isLoading: false,
          onboardingDone: false,
        }));

        sentryLog.error("Logout user - API error", describeErrorForLog(err));

        clearAccessToken();
        window.location.reload();
      }
    } else {
      //if no token check if t query param is set

      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      const tempToken = params.get("t");

      if (tempToken) {
        try {
          markStep("verify-temp-token");
          const { token } = await verifyToken({ t: tempToken });

          setAccessToken(token);

          refetchFeatures();

          window.location.href = "/";
        } catch (err) {
          console.log(err);
        }
      } else {
        const language =
          getLocaleSearchParam(params) ??
          getClosestSupportedLocale(preferredLanguage.userPreferredLanguage) ??
          getClosestSupportedLocale(
            preferredLanguage.clientPreferredLanguage,
          ) ??
          getBrowserPreferredLocale();

        markStep("set-preferred-language");
        await setPreferredLanguage({
          userPreferredLanguage: language,
          clientPreferredLanguage: language,
        });

        clearAccessToken();
      }

      setClientState({ ...clientState, isLoading: false });
    }
  };

  useEffect(() => {
    checkAuth();

    queryClient.invalidateQueries();

    axios.defaults.headers.common["x-app-version"] =
      import.meta.env.VITE_APP_VERSION;
    axios.defaults.withCredentials = true;
  }, []);

  // Return to login the moment the live token is gone while authenticated — on
  // mount, on a cross-tab storage change, and on focus (DEV-8683).
  useEffect(() => {
    if (!clientState.isAuth) return;

    const reloginIfTokenLost = () => {
      if (getCleanAccessToken() === "") {
        forceRelogin("token-cleared-while-authenticated");
      }
    };

    reloginIfTokenLost();
    window.addEventListener("storage", reloginIfTokenLost);
    window.addEventListener("focus", reloginIfTokenLost);

    return () => {
      window.removeEventListener("storage", reloginIfTokenLost);
      window.removeEventListener("focus", reloginIfTokenLost);
    };
  }, [clientState.isAuth]);

  useEffect(() => {
    const lang =
      preferredLanguage.userPreferredLanguage ??
      preferredLanguage.clientPreferredLanguage ??
      getBrowserPreferredLocale();
    axios.defaults.headers.common["x-custom-locale"] = lang;
  }, [preferredLanguage]);

  useEffect(() => {
    // Set the initial page path
    setPathPageView("/");
    // Track the page view
    setTrackPageView();
  }, [setPathPageView, setTrackPageView]);

  const setOnboardingDone = (value: boolean) => {
    setClientState((prevState) => ({
      ...prevState,
      onboardingDone: value,
      isLoading: false,
    }));

    if (value === true) {
      Sentry.captureMessage("Onboarding completed!");
    }
  };

  const setisAuth = (value: boolean) => {
    setClientState((prevState) => ({
      ...prevState,
      isAuth: value,
      isLoading: false,
    }));
  };

  const setUserToken = (value: string) => {
    setClientState((prevState) => ({
      ...prevState,
      token: value,
      isLoading: false,
    }));
  };

  const redirect = async () => {
    const [, , hash] = pathname.split("/");
    const queryFormHash = searchParams.get("form");

    const preselectedLocation = searchParams.get("location");
    const preselectedService = searchParams.get("service");
    const language = getLocaleSearchParam(searchParams);

    try {
      const response = await api.redirect.getRedirectLink({
        hash: hash ?? queryFormHash,
        preselectedLocation: preselectedLocation
          ? Number(preselectedLocation)
          : undefined,
        preselectedService: preselectedService
          ? Number(preselectedService)
          : undefined,
        language: (language ??
          undefined) as GetRedirectLink["query"]["language"],
      });

      if (response) {
        window.location.href = response.redirectUrl;
      }
    } catch (error) {
      // This resolver runs fire-and-forget on page load, so a rejection here
      // becomes an unhandled rejection that Sentry reports as an error. A failed
      // request just means the form link can't be resolved (e.g. the form config
      // for this hash doesn't exist / the link is invalid or expired) — an
      // expected, user-facing condition, not an actionable bug. Log it at warning
      // level instead. Re-throw non-request errors so genuine bugs still surface.
      if (!axios.isAxiosError(error)) throw error;

      sentryLog.warn("Form redirect link could not be resolved", {
        status: error.response?.status,
        hash: hash ?? queryFormHash,
      });
    }
  };

  const redirectReschedule = async () => {
    const customerHash = searchParams.get("customer") || searchParams.get("c");

    if (!customerHash) return;

    const response = await api.redirect.getRedirectRescheduleLink({
      customerHash,
    });

    if (response) {
      window.location.href = response.newUrl;
    }
  };

  if (pathname.includes("/form")) {
    redirect();

    return null;
  }

  if (
    pathname.split("/").includes("r") ||
    pathname.split("/").includes("reschedule")
  ) {
    redirectReschedule();

    return null;
  }

  if (clientState.isLoading || isFeaturesLoading) {
    return (
      <HeroUIProvider>
        {isStartupStalled ? (
          <StartupRecovery />
        ) : (
          <div className="mt-10 flex justify-center">
            <Spinner size="lg" />
          </div>
        )}
      </HeroUIProvider>
    );
  }

  // not authenticated
  if (!clientState.isAuth) {
    const authRedirectSearch = searchParams.toString();
    const authRedirectTo = {
      pathname: "/login",
      search: authRedirectSearch ? `?${authRedirectSearch}` : "",
    };

    return (
      <HeroUIProvider navigate={navigate} useHref={useHref} locale={locale}>
        {/* Default placement is bottom on this provider — clear the iOS
            home indicator. */}
        <ToastProvider
          toastOffset={10 + safeAreaInsets.bottom}
          toastProps={{
            timeout: import.meta.env.VITE_IS_E2E_TEST === "true" ? 100 : 3000,
          }}
        />

        <ConsentGate />

        <SentryRoutes>
          <Route path={"/forgot-password"} element={<ForgotPassword />} />
          <Route path={"/reset-password"} element={<ResetPassword />} />
          <Route
            path={"/login"}
            element={
              <Login
                setOnboardingDone={setOnboardingDone}
                setAuth={setisAuth}
                setUserToken={setUserToken}
              />
            }
          />
          <Route path={"/register"} element={<Register />} />
          <Route path={"/register/:locale"} element={<Register />} />
          <Route path={"/maintenance"} element={<Maintenance />} />

          <Route path={"/sw-reset"} element={<SwReset />} />

          <Route
            path={"*"}
            element={<Navigate to={authRedirectTo} replace />}
          />
        </SentryRoutes>
      </HeroUIProvider>
    );
  }

  if (clientState.token == "")
    return (
      <HeroUIProvider navigate={navigate} useHref={useHref} locale={locale}>
        <Spinner />
      </HeroUIProvider>
    );

  // ----- IS AUTHENTICATED -----
  return (
    <HeroUIProvider navigate={navigate} useHref={useHref} locale={locale}>
      <MfaReauthProvider>
        <AppVersionChecker />

        <Helmet>
          <title>
            Lime Booking
            {accountInfo?.businessName ? ` - ${accountInfo.businessName}` : ""}
          </title>
        </Helmet>

        {/* Mobile toasts sit at the top — keep them below the status bar /
            notch; desktop toasts at the bottom clear the home indicator
            (insets are 0 in regular browsers). */}
        <ToastProvider
          toastOffset={
            10 + (isWideViewport ? safeAreaInsets.bottom : safeAreaInsets.top)
          }
          toastProps={{
            timeout: import.meta.env.VITE_IS_E2E_TEST === "true" ? 100 : 3000,
          }}
          placement={isWideViewport ? "bottom-right" : "top-center"}
        />

        <RouteChangeTracker
          setPathPageView={setPathPageView}
          setTrackPageView={setTrackPageView}
        />

        <ConsentReconciler />
        <ConsentGate />
        <AdminFeedback />

        <div className="App">
          <SentryRoutes>
            <Route path={"/dashboard/*"} element={<Dashboard />}></Route>
            <Route path={"/maintenance"} element={<Maintenance />} />

            <Route path={"/sw-reset"} element={<SwReset />} />

            <Route path={"*"} element={<Navigate to={"/dashboard"} />} />
          </SentryRoutes>
        </div>
      </MfaReauthProvider>
    </HeroUIProvider>
  );
}

function RouteChangeTracker({
  setPathPageView,
  setTrackPageView,
}: {
  setPathPageView: (path: string) => void;
  setTrackPageView: () => void;
}) {
  const location = useLocation();

  useEffect(() => {
    setPathPageView(location.pathname);
    setTrackPageView();
  }, [location, setPathPageView, setTrackPageView]);

  return null;
}

export default function AppWithHeroUIv3Locale() {
  const { i18n } = useLingui();
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <I18nProvider locale={i18n.locale}>
        <App />
      </I18nProvider>
    </ThemeProvider>
  );
}
